import React, { useState } from 'react';
import { FaRobot, FaDownload, FaCheckCircle } from 'react-icons/fa';
import { GiBrain } from 'react-icons/gi';
import {
  ChatItemView,
  ChatAttachmentView,
} from '../../hooks/useTeamChatSocket';
import { InteractionPrompt } from './InteractionPrompt';
import { markdownToSafeHtml } from '../../lib/markdown';
import type { CardRef, VoiceConversationCostRollup } from '../../client';
import { cardUrl } from '../../lib/card-refs';
import { Link } from 'react-router-dom';
import {
  agentUrl,
  renderMarkdownWithSuppressedMentions,
  renderWithSuppressedMentions,
  type MentionableAgent,
} from '../../lib/mentions';
import type { WikiRefProject } from '../../lib/wiki-refs';
import { expandAllRefs, renderPlainTextWithRefs } from '../../lib/rich-refs';
import { useUserContext } from '../../state/UserContext';
import { formatSentTime } from '../../lib/relative-time';
import { costTooltip, formatCostCompact } from '../../utils/formatCost';
import ChatThinkingView, { type HistoryToolPill } from './ChatThinkingView';
import ToolCallModal, {
  toolCallsUrl,
  type ToolCallDetail,
} from './ToolCallModal';
import MemberName from '../MemberName';
import Modal from '../Modal';
import { sessionFetch } from '../../session-client';
import { formatVoiceSessionCost, type VoiceCostState } from './voiceCost';
import ChatChangeReceipts from './ChatChangeReceipts';

interface ChatItemProps {
  item: ChatItemView;
  /** Project card references for #KEY-N display expansion */
  cardRefs?: CardRef[];
  /** The team's projects, so wiki:KEY/path and [[KEY:path]] resolve */
  projects?: WikiRefProject[];
  /** Agent roster, so @mentions can link to each agent's details page */
  agents?: MentionableAgent[];
  teamId?: string;
  /** The open conversation's id — needed to fetch a tool call's details */
  conversationId?: number;
  /** Send a reply (answers a seek-feedback / permission prompt, resumes the agent) */
  onRespond?: (text: string) => void;
  /** True only for the newest item, so a stale prompt can't be re-answered */
  interactionNewest?: boolean;
  /** Persist a permission prompt's grant/decline on its message */
  onResolveInteraction?: (
    messageId: number,
    status: 'granted' | 'declined',
  ) => void;
  voiceCost?: VoiceConversationCostRollup | null;
  voiceCostState?: VoiceCostState;
}

const PendingDots: React.FC = () => (
  <div className="chat-pending" data-testid="chat-pending-indicator">
    <GiBrain className="chat-thinking-icon" />
    <span className="chat-pending-dots" aria-label="Agent is thinking">
      <span />
      <span />
      <span />
    </span>
  </div>
);

/** Images preview inline, audio gets a player; other files are download links */
const Attachments: React.FC<{
  attachments: ChatAttachmentView[];
  teamId?: string;
}> = ({ attachments, teamId }) => {
  const urlFor = (attachment: ChatAttachmentView): string | null => {
    if (!teamId || !attachment.attachmentId) return null;
    return `/api/team/${teamId}/chat-attachments/${attachment.attachmentId}?mimeType=${encodeURIComponent(
      attachment.mimeType,
    )}&filename=${encodeURIComponent(attachment.filename)}`;
  };

  return (
    <div className="chat-user-attachments">
      {attachments.map((attachment, index) => {
        const url = urlFor(attachment);
        const isImage = attachment.mimeType.startsWith('image/');
        const isAudio = attachment.mimeType.startsWith('audio/');
        if (isAudio && url) {
          // A player, not a download link: generated narration or music is
          // meant to be heard in place, and a filename tells you nothing
          // about whether it came out right.
          return (
            <audio
              key={`${attachment.filename}-${index}`}
              className="chat-attachment-audio"
              controls
              preload="metadata"
              src={url}
              title={attachment.filename}
              data-testid={`chat-attachment-audio-${attachment.attachmentId}`}
            />
          );
        }
        if (isImage && url) {
          return (
            <a
              key={`${attachment.filename}-${index}`}
              className="chat-attachment-image"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              title={`${attachment.filename} — open full size`}>
              <img src={url} alt={attachment.filename} loading="lazy" />
            </a>
          );
        }
        // PDFs and other files: a download chip with the file name
        const chip = (
          <>
            <FaDownload size={11} />
            <span className="chat-attachment-file-name">
              {attachment.filename}
            </span>
          </>
        );
        return url ? (
          <a
            key={`${attachment.filename}-${index}`}
            className="chat-attachment-file"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            download={attachment.filename}
            title={`Download ${attachment.filename}`}>
            {chip}
          </a>
        ) : (
          <span
            key={`${attachment.filename}-${index}`}
            className="chat-attachment-file"
            title={attachment.filename}>
            {chip}
          </span>
        );
      })}
    </div>
  );
};

/**
 * One conversation entry: either a participant's prompt bubble, or one
 * agent's response. Other participants' messages are left-aligned with
 * their name; every message shows when it was sent.
 */
const ChatItem: React.FC<ChatItemProps> = ({
  item,
  cardRefs,
  projects,
  agents,
  teamId,
  conversationId,
  onRespond,
  interactionNewest,
  onResolveInteraction,
  voiceCost,
  voiceCostState = 'ready',
}) => {
  const { user } = useUserContext();
  // Only the identity of the open tool is snapshotted; the input/output and
  // pending/finished state are re-derived from `item.segments` on every
  // render (below), so a modal left open on a still-`started` tool updates
  // live to show the output the moment its `tool_activity` succeeds.
  const [openTool, setOpenTool] = useState<{
    toolCallId: string;
    functionName: string;
  } | null>(null);
  // Declared before the divider early return — a hook after a conditional
  // return crashes React with "Rendered more hooks than during the
  // previous render" (see ContinuationApproval in ChatSurface).
  const [summaryOpen, setSummaryOpen] = useState(false);

  if (item.kind === 'divider') {
    // A compaction marker (it carries a summary and/or a summarize cost)
    // renders as "Compacted – $x.xx" and opens the summary on click;
    // other system markers (pause notices) render their own text.
    const isCompaction = !!item.dividerSummary || item.dividerCostUsd != null;
    const cost = formatCostCompact(item.dividerCostUsd);
    return (
      <>
        <div
          className="chat-compacted-divider"
          data-testid="chat-compacted-divider">
          {isCompaction ? (
            <button
              className="chat-compacted-divider-text chat-compacted-divider-button"
              data-testid="chat-compacted-divider-button"
              onClick={() => item.dividerSummary && setSummaryOpen(true)}
              title={
                item.dividerSummary ? 'Show the compaction summary' : undefined
              }>
              {`Compacted${cost ? ` – ${cost}` : ''}`}
            </button>
          ) : (
            <div
              className="chat-compacted-divider-text markdown-content"
              dangerouslySetInnerHTML={{
                __html: markdownToSafeHtml(
                  item.dividerText || 'Conversation compacted',
                ),
              }}
            />
          )}
          {summaryOpen && item.dividerSummary && (
            <Modal
              isOpen
              onClose={() => setSummaryOpen(false)}
              title="Conversation compacted"
              subtitle={cost ? `Summarize call cost ${cost}` : undefined}
              maxWidth="640px"
              scrollBody>
              <div
                className="chat-compaction-summary markdown-content"
                data-testid="chat-compaction-summary"
                dangerouslySetInnerHTML={{
                  __html: markdownToSafeHtml(item.dividerSummary),
                }}
              />
            </Modal>
          )}
        </div>
        {item.changeReceipts && (
          <ChatChangeReceipts receipts={item.changeReceipts} />
        )}
      </>
    );
  }

  // A hand-off notice: the form-assist chat's form was submitted and the
  // thing now exists. Deliberately not a bubble — nobody said it — and not
  // the hairline compaction divider either, since it reports a real event.
  if (item.kind === 'notice') {
    if (item.systemEvent?.type === 'artifact_created') {
      const { artifact } = item.systemEvent;
      const available = artifact.availability === 'active';
      const streamUrl = teamId
        ? `/api/teams/${teamId}/content-files/${artifact.contentFileId}/stream`
        : undefined;
      return (
        <div className="chat-notice chat-artifact-notice" role="note">
          <div>
            <FaCheckCircle size={12} aria-hidden />{' '}
            {item.agentName || `Agent ${item.systemEvent.agentId}`} created{' '}
            {available && streamUrl ? (
              <a href={streamUrl} target="_blank" rel="noopener noreferrer">
                {artifact.name}
              </a>
            ) : (
              <span>{artifact.name}</span>
            )}
            {!available && ` (${artifact.availability})`}
          </div>
          {available && streamUrl && (
            <video
              controls
              preload="metadata"
              src={streamUrl}
              title={artifact.name}
              data-testid="chat-artifact-video"
            />
          )}
        </div>
      );
    }
    if (item.voiceLifecycle) {
      const isEnded = item.voiceLifecycle.event === 'ended';
      const lifecycleBase = isEnded ? 'Voice chat ended' : 'Voice chat started';
      const lifecycleAgentName = item.voiceLifecycle.agentName;
      const lifecycleAgentId = item.voiceLifecycle.agentId;
      const session = voiceCost?.sessions.find(
        (candidate) => candidate.sessionId === item.voiceLifecycle?.sessionId,
      );
      const formatted = formatVoiceSessionCost(session, voiceCostState, false);
      const accessibleText = [
        lifecycleAgentName
          ? `Voice chat with ${lifecycleAgentName} ${item.voiceLifecycle.event}`
          : lifecycleBase,
        isEnded ? formatted.audio : '',
      ]
        .filter(Boolean)
        .join(' · ');
      const lifecycleContent = lifecycleAgentName ? (
        <span>
          Voice chat with{' '}
          {teamId && lifecycleAgentId ? (
            <Link
              to={`/console/team/${teamId}/agent/${lifecycleAgentId}/metrics`}>
              {lifecycleAgentName}
            </Link>
          ) : (
            lifecycleAgentName
          )}{' '}
          {item.voiceLifecycle.event}
        </span>
      ) : (
        <span>{item.noticeText || lifecycleBase}</span>
      );
      return (
        <div
          className="chat-notice chat-voice-lifecycle"
          aria-label={accessibleText}
          aria-live={isEnded ? 'polite' : undefined}
          role="note">
          {lifecycleContent}
          {isEnded && (
            <span className="chat-voice-lifecycle-cost">{formatted.audio}</span>
          )}
        </div>
      );
    }
    /*
     * The record's NAME is the link, not the bare #KEY-N token: the row is
     * read as a sentence ("Card Bake a Cake was created"), and a reference
     * beside the name it already spells out is noise. Falls back to the flat
     * sentence when the reference is not in this conversation's ref table —
     * a name with no destination should not look clickable.
     */
    const noticeRef =
      item.noticeReference && teamId
        ? cardRefs?.find((ref) => ref.reference === item.noticeReference)
        : undefined;
    const linkable = noticeRef?.projectId != null && noticeRef?.cardId != null;
    return (
      <>
        <div className="chat-notice" data-testid="chat-notice">
          <FaCheckCircle size={12} aria-hidden />
          {linkable ? (
            <span>
              {item.noticeNoun}{' '}
              <Link
                to={cardUrl(
                  teamId!,
                  noticeRef as Parameters<typeof cardUrl>[1],
                )}
                data-testid="chat-notice-link">
                {item.noticeName}
              </Link>{' '}
              was created.
            </span>
          ) : (
            <span>{item.noticeText}</span>
          )}
        </div>
        {item.changeReceipts && (
          <ChatChangeReceipts receipts={item.changeReceipts} />
        )}
      </>
    );
  }

  if (item.kind === 'user') {
    // Another participant's message (its author differs from the current
    // user): left-aligned grey bubble with the name above. Your own: the
    // right-aligned accent bubble.
    const fromOther =
      item.authorUserId != null &&
      user?.id != null &&
      Number(item.authorUserId) !== Number(user.id);
    const sentTime = formatSentTime(item.timestamp);
    return (
      <div className={`chat-turn${fromOther ? ' chat-turn-other' : ''}`}>
        {fromOther && (
          <div className="chat-user-author" data-testid="chat-user-author">
            {teamId ? (
              <MemberName teamId={teamId} userId={Number(item.authorUserId)}>
                {item.authorName || 'Team member'}
              </MemberName>
            ) : (
              item.authorName || 'Team member'
            )}
            {sentTime && <span className="chat-sent-time"> · {sentTime}</span>}
          </div>
        )}
        <div className="chat-user-row">
          <div
            className={`chat-user-bubble${fromOther ? ' chat-user-bubble-other' : ''}`}>
            {item.attachments && item.attachments.length > 0 && (
              <Attachments attachments={item.attachments} teamId={teamId} />
            )}
            <div className="chat-user-text">
              {renderWithSuppressedMentions(
                item.userText || '',
                item.suppressedMentions,
                (text, key) => (
                  <React.Fragment key={key}>
                    {teamId
                      ? renderPlainTextWithRefs(
                          text,
                          { teamId, agents, cardRefs, projects },
                          (match, mentionKey) =>
                            // Negative ids are team members (see TeamChat's
                            // roster mapping): their mentions open the member
                            // detail modal instead of an agent page
                            match.agentId < 0 ? (
                              <MemberName
                                key={mentionKey}
                                teamId={teamId}
                                userId={-match.agentId}
                                className="chat-mention">
                                {match.text}
                              </MemberName>
                            ) : (
                              <Link
                                key={mentionKey}
                                to={agentUrl(teamId, match.agentId)}
                                className="chat-mention">
                                {match.text}
                              </Link>
                            ),
                        )
                      : text}
                  </React.Fragment>
                ),
              )}
            </div>
          </div>
          {item.sendStatus === 'sending' && (
            <span className="chat-sending-badge">Sending…</span>
          )}
        </div>
        {!fromOther && sentTime && (
          <div className="chat-user-time chat-sent-time">{sentTime}</div>
        )}
        {/* Waiting for the persistence ack: show the pending indicator */}
        {item.sendStatus === 'sending' && <PendingDots />}
      </div>
    );
  }

  const isLive = item.status === 'processing';
  const sentTime = formatSentTime(item.timestamp);
  const expand = (markdown: string) => {
    if (!teamId) return markdown;
    return expandAllRefs(markdown, { teamId, agents, cardRefs, projects });
  };
  const responseSource =
    item.finalContent !== undefined
      ? item.finalContent
      : item.streamingContent
        ? item.streamingContent
        : undefined;
  const response =
    responseSource !== undefined
      ? renderMarkdownWithSuppressedMentions(
          responseSource,
          item.finalContent !== undefined ? item.suppressedMentions : undefined,
          (text) => markdownToSafeHtml(expand(text)),
        )
      : null;
  const isPending =
    isLive &&
    !item.thinking &&
    item.toolActivity.length === 0 &&
    !item.streamingContent;

  // A reloaded turn's `segments` carry thought text but no `tool`-kind
  // entries (the wire history has no per-call data). For that case only —
  // never for a live turn, which already renders its tool calls inline —
  // fetch this turn's tool calls lazily when "View thoughts" is expanded.
  const hasInlineToolSegments = item.segments.some(
    (segment) => segment.kind === 'tool',
  );
  const canLoadHistoryToolCalls =
    !isLive &&
    item.status === 'complete' &&
    !!teamId &&
    !!conversationId &&
    !!item.assistantMessageId &&
    !hasInlineToolSegments;
  const loadHistoryToolCalls = canLoadHistoryToolCalls
    ? async (): Promise<HistoryToolPill[]> => {
        const response = await sessionFetch(
          toolCallsUrl(teamId!, conversationId!, item.assistantMessageId!),
          { credentials: 'include' },
        );
        if (!response.ok) {
          throw new Error(`Request failed (${response.status})`);
        }
        const data: { toolCalls?: ToolCallDetail[] } = await response.json();
        return (data.toolCalls || [])
          .filter((call) => call.toolCallId !== null)
          .map((call) => ({
            toolCallId: call.toolCallId as string,
            functionName: call.name,
            status: call.status,
          }));
      }
    : undefined;

  return (
    <div className="chat-turn chat-agent-turn">
      <div className="chat-agent-label" data-testid="chat-agent-label">
        <FaRobot size={11} />
        {teamId && item.agentId ? (
          <Link
            className="chat-agent-label-name"
            to={`/console/team/${teamId}/agent/${item.agentId}/metrics`}>
            {item.agentName || 'Agent'}
          </Link>
        ) : (
          <span className="chat-agent-label-name">
            {item.agentName || 'Agent'}
          </span>
        )}
        {item.trigger === 'agent' && item.triggeredByAgentName && (
          <span className="chat-agent-label-trigger">
            · responding to {item.triggeredByAgentName}
          </span>
        )}
      </div>

      {isPending && <PendingDots />}

      <ChatThinkingView
        segments={item.segments}
        live={isLive}
        onOpenTool={(toolCallId, functionName) =>
          setOpenTool({ toolCallId, functionName })
        }
        loadHistoryToolCalls={loadHistoryToolCalls}
      />

      {openTool &&
        (() => {
          // Re-derive the clicked tool's live data on EVERY render (this
          // block re-runs whenever `item` updates). A live turn's tool
          // segment carries its own input/output (streamed via
          // tool_activity — see useTeamChatSocket), so the modal renders it
          // inline with no fetch, and a modal opened while the tool was
          // still `started` flips from the pending note to the real output
          // as soon as the succeeded frame updates the segment. A reloaded
          // turn's pills come from loadHistoryToolCalls, never from
          // segments, so `seg` is undefined and the modal fetches by
          // messageId instead.
          const seg = item.segments.find(
            (segment) =>
              segment.kind === 'tool' &&
              segment.toolCallId === openTool.toolCallId,
          );
          const hasInline = !!seg;
          if (
            !hasInline &&
            !(teamId && conversationId && item.assistantMessageId)
          ) {
            return null;
          }
          return (
            <ToolCallModal
              teamId={teamId}
              conversationId={conversationId}
              messageId={item.assistantMessageId}
              toolCallId={openTool.toolCallId}
              functionName={openTool.functionName}
              hasInline={hasInline}
              inlineInput={seg && seg.kind === 'tool' ? seg.input : undefined}
              inlineOutput={seg && seg.kind === 'tool' ? seg.output : undefined}
              outputPending={
                !!seg && seg.kind === 'tool' && seg.status === 'started'
              }
              summary={seg && seg.kind === 'tool' ? seg.summary : undefined}
              durationMs={
                seg && seg.kind === 'tool' ? seg.durationMs : undefined
              }
              onClose={() => setOpenTool(null)}
            />
          );
        })()}

      {response && (
        <div
          className={`chat-agent-response markdown-content ${isLive ? 'chat-agent-response-streaming' : ''}`}>
          {response}
        </div>
      )}

      {item.changeReceipts && (
        <ChatChangeReceipts receipts={item.changeReceipts} />
      )}

      {(sentTime || formatCostCompact(item.costUsd)) && (
        <div className="chat-agent-metadata">
          {sentTime && <span className="chat-sent-time">{sentTime}</span>}
          {sentTime && formatCostCompact(item.costUsd) && (
            <span aria-hidden>·</span>
          )}
          {formatCostCompact(item.costUsd) && (
            <span
              className="chat-msg-cost"
              data-testid="chat-msg-cost"
              title={costTooltip(
                item.tokensSent,
                item.tokensReceived,
                item.costUsd,
              )}>
              {formatCostCompact(item.costUsd)}
            </span>
          )}
        </div>
      )}

      {/* Images the agent generated this turn. Rendered after the prose so
          "here is the logo" is followed by the logo, not preceded by it. */}
      {item.attachments && item.attachments.length > 0 && (
        <div className="chat-agent-attachments">
          <Attachments attachments={item.attachments} teamId={teamId} />
        </div>
      )}

      {item.interaction && item.status === 'complete' && onRespond && (
        <InteractionPrompt
          interaction={item.interaction}
          agentId={item.agentId}
          agentName={item.agentName}
          onRespond={onRespond}
          newest={!!interactionNewest}
          teamId={teamId}
          onResolve={
            item.interactionMessageId !== undefined && onResolveInteraction
              ? (status) =>
                  onResolveInteraction(item.interactionMessageId!, status)
              : undefined
          }
        />
      )}

      {item.status === 'error' && (
        <div
          className="chat-turn-error markdown-content"
          dangerouslySetInnerHTML={{
            __html: markdownToSafeHtml(
              item.errorMessage ||
                'Something went wrong processing this message.',
            ),
          }}
        />
      )}

      {item.status === 'paused' && (
        <div
          className="chat-turn-paused markdown-content"
          data-testid="chat-turn-paused"
          dangerouslySetInnerHTML={{
            __html: markdownToSafeHtml(item.errorMessage || 'Paused.'),
          }}
        />
      )}
    </div>
  );
};

export default ChatItem;
